import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CreateFirmPayload,
  Firm,
  FirmFilters,
  UpdateFirmPayload,
} from '../types/firms-types';
import { baseQueryWithErrorHandling } from './baseQuery';
import {
  AddressSchema,
  AssigneeRole,
  // Backfill types
  BackfillProgressSchema,
  CreateAddressSchema,
  CreateExternalTaskRuleSchema,
  CreateFirmRoleAssignmentSchema,
  // Dialpad phone number types
  DialpadPhoneNumberListResponse,
  DialpadPhoneNumberOption,
  // Distinct task names types
  DistinctExternalTaskNameSchema,
  enhancedApi as generatedFirmApi,
  ExternalTaskFilter,
  // Preview filter types
  ExternalTaskPreviewSchema,
  // External Task Rule types
  ExternalTaskRuleSchema,
  ExternalTaskRuleStatus,
  ExternalTaskRuleType,
  ExternalTaskRuleVersionSchema,
  FirmRoleAssignmentDetailSchema,
  // Firm Task Template types
  FirmTaskTemplateResponse,
  InternalTaskFilter,
  InternalTaskPreviewSchema,
  LiveTestMatchedTaskSchema,
  LiveTestMatchSchema,
  LiveTestRequestSchema,
  LiveTestResultSchema,
  PreviewExternalFilterRequestSchema,
  PreviewExternalFilterResponseSchema,
  PreviewInternalFilterRequestSchema,
  PreviewInternalFilterResponseSchema,
  RuleBackfillRunSchema,
  RuleBackfillRunStatus,
  RuleConfig,
  TriggerTaskType,
  UpdateExternalTaskRuleConfigSchema,
  UpdateExternalTaskRuleSchema,
  UpdateFirmRoleAssignmentSchema,
} from './generated/firmsService';

/** Billing code usage stat returned by the firm billing-code-stats endpoint */
export interface BillingCodeStat {
  code: string;
  count: number;
  total: number;
}

/** Config for FirmRoleAssignment - settings that apply to this role assignment */
export interface FirmRoleAssignmentConfig {
  /** Whether this case manager should be eligible for auto-assignment of new cases */
  case_auto_assign?: boolean;
}

/** Extended CreateFirmRoleAssignmentSchema with config support */
export interface CreateFirmRoleAssignmentWithConfig {
  firm_id: string;
  role: AssigneeRole;
  assignee_id: string;
  /** Configuration options for this role assignment */
  config?: FirmRoleAssignmentConfig;
}

/** Extended UpdateFirmRoleAssignmentSchema with config support */
export interface UpdateFirmRoleAssignmentWithConfig {
  assignee_id: string;
  /** Configuration options for this role assignment */
  config?: FirmRoleAssignmentConfig;
}

/** Extended FirmRoleAssignmentDetailSchema with config */
export interface FirmRoleAssignmentDetailWithConfig
  extends FirmRoleAssignmentDetailSchema {
  config?: FirmRoleAssignmentConfig;
}

/** Extended config including weight and availability window for team roles */
export interface FirmRoleAssignmentFullConfig {
  case_auto_assign?: boolean;
  /** Relative assignment weight (0 = excluded, 1.0 = default). */
  weight?: number;
  /** ISO date from which the member is available. null/undefined = always. */
  available_from?: string | null;
  /** ISO date until which the member is available (inclusive). null/undefined = indefinite. */
  available_until?: string | null;
}

/** Assignment detail with the full extended config (weight + availability). */
export interface FirmRoleAssignmentWithWeight
  extends FirmRoleAssignmentDetailSchema {
  config?: FirmRoleAssignmentFullConfig;
}

/** A single member entry when bulk-setting a team distribution. */
export interface TeamMemberPayload {
  assignee_id: string;
  weight: number;
  available_from?: string | null;
  available_until?: string | null;
}

/** Request body for PUT /api/firms/{firm_pk}/role-assignments/team-distribution/ */
export interface SetTeamDistributionPayload {
  firmPk: string;
  role: string;
  members: TeamMemberPayload[];
}

/** A single global (org-wide) role assignment row. */
export interface GlobalRoleAssignment {
  id: string;
  role: string;
  assignee_id: string;
  assignee: { id: string; display_name: string; email: string };
  config?: FirmRoleAssignmentFullConfig;
}

/** Request body for PUT /api/team-distribution/ */
export interface SetGlobalTeamDistributionPayload {
  role: string;
  members: TeamMemberPayload[];
}

// Re-export types from generated API
export type {
  AddressSchema,
  AssigneeRole,
  // Backfill types
  BackfillProgressSchema,
  CreateAddressSchema,
  CreateExternalTaskRuleSchema,
  CreateFirmRoleAssignmentSchema,
  // Dialpad phone number types
  DialpadPhoneNumberListResponse,
  DialpadPhoneNumberOption,
  // Distinct task names types
  DistinctExternalTaskNameSchema,
  ExternalTaskFilter,
  // Preview filter types
  ExternalTaskPreviewSchema,
  // External Task Rule types
  ExternalTaskRuleSchema,
  ExternalTaskRuleStatus,
  ExternalTaskRuleType,
  ExternalTaskRuleVersionSchema,
  FirmRoleAssignmentDetailSchema,
  // Firm Task Template types
  FirmTaskTemplateResponse,
  InternalTaskFilter,
  InternalTaskPreviewSchema,
  LiveTestMatchedTaskSchema,
  LiveTestMatchSchema,
  LiveTestRequestSchema,
  LiveTestResultSchema,
  PreviewExternalFilterRequestSchema,
  PreviewExternalFilterResponseSchema,
  PreviewInternalFilterRequestSchema,
  PreviewInternalFilterResponseSchema,
  RuleBackfillRunSchema,
  RuleBackfillRunStatus,
  RuleConfig,
  TriggerTaskType,
  UpdateExternalTaskRuleConfigSchema,
  UpdateExternalTaskRuleSchema,
  UpdateFirmRoleAssignmentSchema,
};

/** Payload for setting a firm feature flag */
export interface SetFirmFeatureFlagPayload {
  firmId: string;
  name: string;
  value: string;
}

export const firmsApiClient = createApi({
  reducerPath: 'firmsApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Firms', 'FirmCKEditorConfig'] as const,
  endpoints: (builder) => ({
    getFirms: builder.query<Firm[], Partial<FirmFilters>>({
      query: (params) => ({
        url: `api/firms/`,
        params,
      }),
      providesTags: ['Firms'],
    }),
    getFirm: builder.query<Firm, string>({
      query: (id) => `api/firms/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Firms', id }],
    }),
    createFirm: builder.mutation<Firm, CreateFirmPayload>({
      query: (firm) => ({
        url: `api/firms/`,
        method: 'POST',
        body: firm,
      }),
      invalidatesTags: ['Firms'],
    }),
    updateFirm: builder.mutation<Firm, UpdateFirmPayload>({
      query: ({ id, ...updates }) => ({
        url: `api/firms/${id}/`,
        method: 'PATCH',
        body: updates,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Firms', id },
        'Firms',
      ],
    }),
    setFirmLetterhead: builder.mutation<
      Firm,
      { id: string; letterhead: File | null }
    >({
      query: ({ id, letterhead }) => {
        const formData = new FormData();
        if (letterhead) {
          formData.append('letterhead', letterhead);
        }
        return {
          url: `api/firms/${id}/set_letterhead/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Firms', id },
        'Firms',
      ],
    }),
    setFirmFooter: builder.mutation<
      Firm,
      { id: string; firm_footer: File | null }
    >({
      query: ({ id, firm_footer }) => {
        const formData = new FormData();
        if (firm_footer) {
          formData.append('firm_footer', firm_footer);
        }
        return {
          url: `api/firms/${id}/set_firm_footer/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Firms', id },
        'Firms',
      ],
    }),
    setFirmLogo: builder.mutation<
      Firm,
      { id: string; firm_logo: File | null }
    >({
      query: ({ id, firm_logo }) => {
        const formData = new FormData();
        if (firm_logo) {
          formData.append('firm_logo', firm_logo);
        }
        return {
          url: `api/firms/${id}/set_firm_logo/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Firms', id },
        'Firms',
      ],
    }),
    setFirmWatermark: builder.mutation<
      Firm,
      { id: string; firm_watermark: File | null }
    >({
      query: ({ id, firm_watermark }) => {
        const formData = new FormData();
        if (firm_watermark) {
          formData.append('firm_watermark', firm_watermark);
        }
        return {
          url: `api/firms/${id}/set_firm_watermark/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Firms', id },
        'Firms',
      ],
    }),
    setFirmHeaderFooterDocx: builder.mutation<
      Firm,
      { id: string; firm_header_footer_docx: File | null }
    >({
      query: ({ id, firm_header_footer_docx }) => {
        const formData = new FormData();
        if (firm_header_footer_docx) {
          formData.append('firm_header_footer_docx', firm_header_footer_docx);
        }
        return {
          url: `api/firms/${id}/set_firm_header_footer_docx/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Firms', id },
        'Firms',
      ],
    }),
    deactivateFirm: builder.mutation<Firm, string>({
      query: (id) => ({
        url: `api/firms/${id}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Firms', id }, 'Firms'],
    }),
    getFirmBillingCodeStats: builder.query<
      BillingCodeStat[],
      string
    >({
      query: (firmId) => `api/firms/${firmId}/billing-code-stats/`,
      providesTags: (result, error, firmId) => [
        { type: 'Firms', id: `${firmId}-billing-stats` },
      ],
    }),
    setTeamDistribution: builder.mutation<
      FirmRoleAssignmentWithWeight[],
      SetTeamDistributionPayload
    >({
      query: ({ firmPk, role, members }) => ({
        url: `api/firms/${firmPk}/role-assignments/team-distribution/`,
        method: 'PUT',
        body: { role, members },
      }),
    }),
     setFirmFeatureFlag: builder.mutation<
       { name: string; value: string },
       SetFirmFeatureFlagPayload
     >({
       query: ({ firmId, name, value }) => ({
        url: `api/firms/${firmId}/set-feature-flag/`,
        method: 'POST',
        body: { name, value },
      }),
      invalidatesTags: (result, error, { firmId }) => [
        { type: 'Firms', id: firmId },
        'Firms',
      ],
    }),
  }),
});

export const {
  useGetFirmsQuery,
  useGetFirmQuery,
  useCreateFirmMutation,
  useUpdateFirmMutation,
  useSetFirmLetterheadMutation,
  useSetFirmFooterMutation,
  useSetFirmLogoMutation,
  useSetFirmWatermarkMutation,
  useSetFirmHeaderFooterDocxMutation,
  useDeactivateFirmMutation,
  useGetFirmBillingCodeStatsQuery,
  useSetTeamDistributionMutation,
  useSetFirmFeatureFlagMutation,
} = firmsApiClient;

export const enhancedFirmApi = generatedFirmApi.enhanceEndpoints({
  endpoints: {
    setHipaaAttorneyRelationshipForm: {
      query: (queryArg) => {
        const formData = new FormData();
        Object.entries(queryArg.setHipaaAttorneyRelationshipFormSchema).forEach(
          ([key, value]) => {
            if (value) {
              formData.append(key, value);
            }
          },
        );

        return {
          url: `api/firms/${queryArg.id}/set_hipaa_attorney_relationship_form/`,
          method: 'POST',
          body: formData,
        };
      },
    },
    setHipaaReproductiveRightsAttestation: {
      query: (queryArg) => {
        const formData = new FormData();
        Object.entries(
          queryArg.setHipaaReproductiveRightsAttestationSchema,
        ).forEach(([key, value]) => {
          if (value) {
            formData.append(key, value);
          }
        });

        return {
          url: `api/firms/${queryArg.id}/set_hipaa_reproductive_rights_attestation/`,
          method: 'POST',
          body: formData,
        };
      },
    },
    setClientPortalLogo: {
      query: (queryArg) => {
        const formData = new FormData();
        if (queryArg.setClientPortalLogoSchema.client_portal_logo) {
          formData.append(
            'client_portal_logo',
            queryArg.setClientPortalLogoSchema.client_portal_logo,
          );
        }

        return {
          url: `api/firms/${queryArg.id}/set_client_portal_logo/`,
          method: 'POST',
          body: formData,
        };
      },
    },
  },
});

// Export hooks from generated API
export const {
  useListFirmRoleAssignmentsQuery,
  useCreateFirmRoleAssignmentMutation,
  useGetFirmRoleAssignmentQuery,
  useUpdateFirmRoleAssignmentMutation,
  useDeleteFirmRoleAssignmentMutation,
  // Address management hooks
  useGetFirmAddressQuery,
  useSetFirmAddressMutation,
  // External Task Rule hooks
  useExternalTaskRuleListQuery,
  useExternalTaskRuleCreateMutation,
  useExternalTaskRuleRetrieveQuery,
  useExternalTaskRulePartialUpdateMutation,
  useExternalTaskRuleDestroyMutation,
  useExternalTaskRuleUpdateConfigMutation,
  useExternalTaskRuleVersionsQuery,
  useExternalTaskRuleLiveTestMutation,
  // Preview filter hooks
  useExternalTaskRulePreviewExternalFilterMutation,
  useExternalTaskRulePreviewInternalFilterMutation,
  // Backfill hooks
  useExternalTaskRuleStartBackfillMutation,
  useExternalTaskRuleGetBackfillProgressQuery,
  useExternalTaskRuleCancelBackfillMutation,
  useExternalTaskRuleListBackfillsQuery,
  // Distinct task names hook
  useExternalTaskRuleDistinctNamesQuery,
  // Firm Task Templates hooks
  useFirmTaskTemplatesListQuery,
  // Dialpad phone numbers hook
  useFirmDialpadNumbersListQuery,
  // Firm PATCH mutation (from generated API with correct types)
  useFirmsPartialUpdateMutation,
  useFirmsRetrieveQuery,
  // Client Portal Logo
  useSetClientPortalLogoMutation,
} = enhancedFirmApi;
